import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  ReactComponent as ActionIcCheck24px
} from '../../assets/icons/navigation/ic_check_24px.svg';
import {
  ReactComponent as ContentIcRemove24px
} from '../../assets/icons/content/ic_remove_24px.svg';
import {
  ReactComponent as NavigationIcCancel24px
} from '../../assets/icons/navigation/ic_cancel_24px.svg';
import {
  ReactComponent as DeviceIcAccessTime24px
} from '../../assets/icons/device/ic_access_time_24px.svg';
import {
  ReactComponent as AlertIcError24px
} from '../../assets/icons/alert/ic_error_24px.svg';
import { getInitials } from '../../utils/getInitials.util';
import { getBgColor } from '../../utils/getBgColor.util';

const getStatusIcon = statusName => {
  let statusIcon;

  switch (statusName) {
    case 'online':
      statusIcon = <ActionIcCheck24px className={`motif-avatar-status-icon-${statusName}`} />;
      break;
    case 'no-disturb':
      statusIcon = <ContentIcRemove24px className={`motif-avatar-status-icon-${statusName}`} />;
      break;
    case 'no-available':
      statusIcon = <NavigationIcCancel24px className={`motif-avatar-status-icon-${statusName}`} />;
      break;
    case 'absent':
      statusIcon = <DeviceIcAccessTime24px className={`motif-avatar-status-icon-${statusName}`} />;
      break;
    case 'warning':
      statusIcon = <AlertIcError24px className={`motif-avatar-status-icon-${statusName}`} />;
      break;
    default:
      statusIcon = <ContentIcRemove24px className="motif-avatar-status-icon-none" />;
      break;
  }

  return statusIcon;
};

const getStatusLabel = statusName => {
  let statusLabel;

  switch (statusName) {
    case 'online':
      statusLabel = ' - Online';
      break;
    case 'no-disturb':
      statusLabel = ' - Do Not Disturb';
      break;
    case 'no-available':
      statusLabel = ' - Not Available';
      break;
    case 'absent':
      statusLabel = ' - Absent';
      break;
    case 'warning':
      statusLabel = ' - Warning';
      break;
    case 'none':
      statusLabel = ' - None State';
      break;
    default:
      statusLabel = '';
      break;
  }

  return statusLabel;
};

const bgColors = getComputedStyle(document.body)
  .getPropertyValue('--avatar-bg-color-list')
  .split(',');

/**
 * @typedef Props
 * @prop {string} userName
 * @prop {string} [description]
 * @prop {{status: React.ReactNode}} [icons]
 * @prop {'small' | 'large'} [size]
 * @prop  {string} [src]
 * @prop  {'online' | 'no-disturb' | 'no-available' | 'absent' | 'warning' | 'none'} [status]
 * @prop  {string} [className]
 */
/**
 * @param {Props & {[x: string]: any;}} props
 */
const MotifAvatar = (props) => {
  const {
    className = '',
    description = '',
    icons = {},
    size = 'small',
    src = '',
    status = 'none',
    userName,
    ...other
  } = props;
  const classes = classNames({
    'motif-avatar': true,
    [`motif-avatar-status-${status}`]: status,
    [`motif-avatar-size-${size}`]: size,
    [className]: true
  });

  const label = `${userName}${getStatusLabel(status)}`;
  const labelDescription = (description) ? description : label;

  return (
    <div className={classes} {...other} data-motif-avatar>
      {src && src.length ? (
        <img
          alt={labelDescription}
          aria-label={label}
          className="motif-avatar-img"
          src={src}
          title={label}
        />
      ) : (
        <abbr
          className="motif-avatar-initials"
          role="img"
          aria-label={label}
          style={{ background: getBgColor(bgColors) }}
          title={label}
        >
          {userName && getInitials(userName)}
        </abbr>
      )}
      {icons.status || (status && getStatusIcon(status))}
    </div>
  );
};

MotifAvatar.propTypes = {
  className: PropTypes.string,
  description: PropTypes.string,
  icons: PropTypes.shape({
    status: PropTypes.node
  }),
  size: PropTypes.oneOf(['small', 'large']),
  src: PropTypes.string,
  status: PropTypes.oneOf(['online', 'no-disturb', 'no-available', 'absent', 'warning', 'none']),
  userName: PropTypes.string
};

export { MotifAvatar };
